import { useEffect, useState, useRef } from 'react';
import { payAPI } from '../../../../services/redwolfSdkAPI';
import * as Sentry from '@sentry/react';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import { useDispatch, useSelector } from 'react-redux';

import AddCard from './AddCard';
import UpdateCard from './UpdateCard';
import DeleteCard from './DeleteCard';
import ContactInformation from '../../Account/ContactInformation';
import { setCreditCards } from '../../../../store/paySlice';
import { getLocalStorage, getToken } from '../../../../utils/localStorage';
import { isMobile } from '../../../../utils/constants';
import PaymentSkeleton from '../../../common/skeletons/PaymentSkeleton';

const Cards = () => {
	const [isUpdateContactModalOpen, setIsUpdateContactModalOpen] = useState(false);
	const [isCardExpiring, setIsCardExpiring] = useState(false);
	const [cardList, setCardList] = useState([]);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [openUpdateCardModal, setOpenUpdateCardModal] = useState(false);
	const [openDeleteCardModal, setOpenDeleteCardModal] = useState(false);
	const [isSelectedCard, setIsSelectedCard] = useState({});
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const cardContainerFocusRef: any = useRef<HTMLDivElement>(null);
	const cardsTabindex = !isMobile ? { tabIndex: -1 } : {};
	const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
	const {
		account: { user }
	} = useSelector((state: any) => state.user);
	const isRequiresPersonalization = getLocalStorage('isRequiresPersonalization');

	const [requestRedwolfService] = useRequestRedwolfService();
	const dispatch = useDispatch();
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Cards List' });

	useEffect(() => {
		if (cardContainerFocusRef.current) {
			cardContainerFocusRef.current.focus();
		}
		getCreditCards();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openContactInformationModal = (setData: any) => {
		setIsUpdateContactModalOpen(setData);
	};

	const getCreditCards = () => {
		setCardList([]);
		setIsSkeletonLoading(true);

		requestRedwolfService(payAPI.getCreditCards(token))
			.then((response: any) => {
				setIsSkeletonLoading(false);

				if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
					const cards = response.data.toArray();

					const expiresCard = cards.find((card: any) => {
						return card.expirationMessage !== '';
					});

					if (expiresCard) {
						setIsCardExpiring(true);
					} else {
						setIsCardExpiring(false);
					}
					dispatch(setCreditCards(cards));
					setCardList(cards);
				} else {
					Sentry.captureException(new Error('getCreditCards:' + response));
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSkeletonLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const closeUpdateModal = () => {
		setOpenUpdateCardModal(false);
	};

	const openUpdateModal = (card: any, event: any) => {
		event.preventDefault();
		setIsSelectedCard(card);
		setOpenUpdateCardModal(true);
		window.lastFocusElement = event.target.id;
	};

	const backToUpdateModal = () => {
		setOpenUpdateCardModal(true);
	};

	const openDeleteModal = () => {
		setOpenDeleteCardModal(true);
	};

	const closeDeleteModal = () => {
		setOpenDeleteCardModal(false);
	};

	const openAddCardModal = () => {
		setIsAddCardModalOpen(true);
	};

	const closeAddCardModal = () => {
		setIsAddCardModalOpen(false);
	};

	return (
		<>
			<div className="acc-comp-row" ref={cardContainerFocusRef} {...cardsTabindex}>
				<div className="acc-comp-right">
					<button
						className="primary-button-small acc-button"
						disabled={isRequiresPersonalization}
						onClick={openAddCardModal}
						aria-label="Add new card">
						ADD NEW CARD
					</button>
				</div>

				{isAddCardModalOpen && (
					<AddCard
						getCreditCards={getCreditCards}
						isAddCardModalOpen={isAddCardModalOpen}
						closeAddCardModal={closeAddCardModal}
					/>
				)}

				<div className="acc-comp-left validation-error">
					<h2 className="ac-title">
						Payment Methods
						{isCardExpiring && (
							<span className="exclamation-icon error-icon payment-section-icon">
								<em aria-hidden="true" className="icon-error item-icon"></em>
							</span>
						)}
					</h2>

					{isRequiresPersonalization ? (
						<div className="empty">
							<p>
								<span>To add a payment method please </span>
								<span
									className="link"
									aria-label="update your contact information."
									onClick={() => openContactInformationModal(true)}>
									update your contact information.
								</span>
							</p>
						</div>
					) : (
						<div className="cc-card-details">
							{cardList.length > 0 &&
								cardList.map((card: any, index: number) => {
									const expiredDate = card.expirationDate && card.expirationDate.split('/')[0];
									const expiredYear = card.expirationDate && card.expirationDate.split('/')[1];
									const expiredYearLastTwo = expiredYear.slice(-2);

									return (
										<div className="card-items" key={index}>
											<label className="cc-select-info" htmlFor="updateBtn">
												<span className="cc-number" id={`crname-${index}`}>
													{card.cardType === 'American Express'
														? 'AMEX'
														: card.cardType === 'Mastercard'
														? 'MC'
														: 'VISA'}
													....{card.lastFour}
													{card.isDefault && <span className="default-info">Default</span>}
												</span>
												<span className="cc-exp">
													EXP. {expiredDate + '/' + expiredYearLastTwo}
												</span>
												<span className="update-info">
													<a
														href="/"
														role="button"
														className="link updatePaymentBtn"
														id={`update-card-${card.cardToken}`}
														aria-describedby={`crname-${index}`}
														onClick={(event: any) => openUpdateModal(card, event)}>
														Update
													</a>
												</span>
											</label>
											<p className="error-info expiry-msg">{card.expirationMessage}</p>
										</div>
									);
								})}

							{isSkeletonLoading ? (
								<PaymentSkeleton />
							) : (
								cardList.length === 0 && (
									<div className="empty">
										<p>No payment method on file.</p>
									</div>
								)
							)}

							{openUpdateCardModal && (
								<UpdateCard
									openUpdateCardModal={openUpdateCardModal}
									closeModal={closeUpdateModal}
									getCreditCards={getCreditCards}
									selectedCard={isSelectedCard}
									openDeleteCardModal={openDeleteModal}
								/>
							)}
							{openDeleteCardModal && (
								<DeleteCard
									openDeleteCardModal={openDeleteCardModal}
									closeModal={closeDeleteModal}
									getCreditCards={getCreditCards}
									selectedCard={isSelectedCard}
									openUpdateCardModal={backToUpdateModal}
								/>
							)}
						</div>
					)}

					<ContactInformation
						isOpenContactInfoModal={isUpdateContactModalOpen}
						paymentMethod={true}
						handleContactInfoModal={(data: any) => openContactInformationModal(data)}
					/>
				</div>
			</div>
		</>
	);
};

export default Cards;
